import { useEffect } from 'react';
import { Feature } from 'ol';
import { Polygon } from 'ol/geom';
import { Stroke, Style, Fill } from 'ol/style';

import { useProject } from '@contexts/Project.context';
import { useVectorLayer } from '@hooks/useVectorLayer';
import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { AREA_MODE } from '@utils/constants';

const LAYER_ID = 'objectPickerAoiPreview';
const Z_INDEX = 20;

const AOI_STYLE = new Style({
	stroke: new Stroke({
		color: 'rgba(255, 200, 0, 0.9)',
		width: 3,
		lineDash: [8, 6],
	}),
	fill: new Fill({
		color: 'rgba(255, 200, 0, 0.08)',
	}),
});

/**
 * Renders the user-drawn AOI polygon on the map as a preview overlay while the
 * Object Picker wizard is active. Automatically adds/removes the layer based on
 * the store's `aoiPolygon` and `areaMode`.
 *
 * This is mounted alongside ObjectPickerModal in ProjectMapView, so it stays
 * alive even when the modal is hidden during drawing.
 */
const AoiPreviewLayer = () => {
	const { mapObject } = useProject();
	const aoiPolygon = useObjectPickerStore(s => s.aoiPolygon);
	const areaMode = useObjectPickerStore(s => s.areaMode);

	const layer = useVectorLayer(mapObject, LAYER_ID, {
		name: 'Object picker AOI preview',
		zIndex: Z_INDEX,
		visible: false,
	});

	const shouldShow = areaMode === AREA_MODE.DRAW && Boolean(aoiPolygon);

	useEffect(() => {
		if (!layer) return;

		const source = layer.getSource();
		source.clear();

		if (shouldShow) {
			source.addFeature(new Feature({ geometry: new Polygon([aoiPolygon]) }));
		}

		layer.setStyle(AOI_STYLE);
		layer.setVisible(shouldShow);
	}, [layer, shouldShow, aoiPolygon]);

	return null;
};

export default AoiPreviewLayer;
